import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, HStack, Switch, Text } from '@chakra-ui/react';

import { getCompany, selectCompanySettings, updateCompanySetting } from '../../../../redux/modules/company';

const EmailRulesPanel = () => {
  const dispatch = useDispatch();
  const companySettings = useSelector(selectCompanySettings);
  const { companyId } = useParams();

  const recordContactSetting = useMemo(
    () => companySettings.find(setting => setting.name === 'include_record_and_insured_contact_info_in_email_tools'),
    companySettings
  );

  const agentContactSetting = useMemo(
    () => companySettings.find(setting => setting.name === 'include_agent_info_in_email_tools'),
    companySettings
  );

  const emptyEmailTokenSetting = useMemo(
    () => companySettings.find(setting => setting.name === 'email_tokens_display_nothing_when_empty'),
    companySettings
  );

  const [recordState, setRecordState] = useState(recordContactSetting.value);
  const [agentState, setAgentState] = useState(agentContactSetting.value);
  const [emptyTokenState, setEmptyTokenState] = useState(emptyEmailTokenSetting.value);

  useEffect(() => {
    setRecordState(recordContactSetting.value);
  }, [recordContactSetting]);

  useEffect(() => {
    setAgentState(agentContactSetting.value);
  }, [agentContactSetting]);

  useEffect(() => {
    setEmptyTokenState(emptyEmailTokenSetting.value);
  }, [emptyEmailTokenSetting]);

  useEffect(() => () => dispatch(getCompany(companyId)), []);

  const handleOnChange = (settingTypeId, value) => {
    dispatch(updateCompanySetting(
      companyId,
      settingTypeId,
      value,
      value
    ));
  };

  return (
    <Box layerStyle="tabPanel">
      <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
        <HStack justifyContent="space-between">
          <Box pr={4} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Record Contacts</Text>
            <Text color="greyScale.500" textStyle="bodySm">By default, if record contact information exists, PINS will only auto suggest the record contact information instead of the insured contact information when preparing emails.</Text>
            <Text color="greyScale.500" textStyle="bodySm">Turning this setting on will auto suggest both the insured AND record contact information when preparing emails.</Text>
          </Box>
          <Box>
            <Switch
              isChecked={recordState}
              onChange={e => {
                setRecordState(e.target.checked);
                handleOnChange(recordContactSetting.id, e.target.checked);
              }}
            />
          </Box>
        </HStack>
      </Box>

      <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
        <HStack justifyContent="space-between">
          <Box pr={4} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Agents</Text>
            <Text color="greyScale.500" textStyle="bodySm">
              Turning this setting on will auto suggest the email addresses of people who submitted the policies/documents for a given
              record when preparing emails.
            </Text>
            <Text color="greyScale.500" textStyle="bodySm">This only applies to the Bulk Email Editor Tool and Email Automation setting.</Text>
          </Box>
          <Box>
            <Switch
              isChecked={agentState}
              onChange={e => {
                setAgentState(e.target.checked);
                handleOnChange(agentContactSetting.id, e.target.checked);
              }}
            />
          </Box>
        </HStack>
      </Box>

      <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
        <HStack justifyContent="space-between">
          <Box pr={4} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Empty Email Tokens</Text>
            <Text color="greyScale.500" textStyle="bodySm">
              Turning this setting on will leave an email token completely empty when the value of the token has no results
              instead of displaying the word &#34;None&#34; within emails.
            </Text>
          </Box>
          <Box>
            <Switch
              isChecked={emptyTokenState}
              onChange={e => {
                setEmptyTokenState(e.target.checked);
                handleOnChange(emptyEmailTokenSetting.id, e.target.checked);
              }}
            />
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

export default EmailRulesPanel;
